import moment from "moment";
import { getAppointmentStatusColor, getAppointmentStatusLabel } from "@phurshell/psiapp-frontend-core/data";
import type { Appointment } from "@phurshell/psiapp-frontend-core/domain";
import { toPsiView } from "./psi";

/**
 * Sessão no formato que as telas do main usam, a partir do `Appointment` do core.
 *
 * O `AppointmentMapperImpl.toDomain` do core foi feito para o pro: textos de status do
 * ponto de vista do psicólogo, `paymentError` com outra regra, `chat.psychologist` só
 * com id/nome. Ele mantém os campos crus (`...data`), então aqui as regras do
 * `prepareAppointment` legado são reaplicadas por cima.
 * Equivalência com o legado: tests/unit/formatters/appointment.spec.ts (respostas reais).
 * As regras abaixo são a fonte única: AppointmentService/RescheduleService delegam para cá.
 */

export const APPOINTMENT_STATUS = {
  AGUARDANDO: "aguardando",
  AGENDADO: "agendado",
  CONCLUIDO: "concluido",
  CANCELADO: "cancelado",
  RECUSADO: "recusado",
  ANDAMENTO: "em andamento",
};

export const APPOINTMENT_TYPES = {
  IMEDIATE: "imediate",
  SCHEDULED: "scheduled",
};

export const APPOINTMENT_FREQUENCY = {
  SINGLE: "daily",
  WEEKLY: "weekly",
  FORTNIGHT: "fortnight",
  MONTHLY: "monthly",
};

export const RESCHEDULE_STATUS = {
  PENDING: "pending",
  CANCELED: "canceled",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
};

export function formatAppointmentStatus(statusName: string, breakLine = false) {
  switch (statusName) {
    case APPOINTMENT_STATUS.AGUARDANDO:
      return {
        name: breakLine ? "Aguardando\nconfirmação" : "Aguardando confirmação",
        color: "text-warning",
        icon: "fas fa-clock",
      };
    case APPOINTMENT_STATUS.AGENDADO:
      return { name: "Confirmado pelo psi", color: "text-success", icon: "fas fa-check-circle" };
    case APPOINTMENT_STATUS.CONCLUIDO:
      return { name: "Finalizada", color: "text-primary50" };
    case APPOINTMENT_STATUS.CANCELADO:
    case APPOINTMENT_STATUS.RECUSADO:
      return { name: "Cancelado", color: "text-primary50" };
    case APPOINTMENT_STATUS.ANDAMENTO:
      return { name: "Em andamento", color: "text-info" };
    default:
      return { name: "Sem status", color: "primary" };
  }
}

export function formatRescheduleStatus(statusName: string, madeByPsi: boolean) {
  const by = !madeByPsi ? "parte do psi" : "você";
  switch (statusName) {
    case RESCHEDULE_STATUS.PENDING:
      return { name: "Reagendamento pendente", color: "text-primary", bgColor: "bg-primary05", icon: "fas fa-clock", text: `encontra-se aguardando confirmação por ${by}` };
    case RESCHEDULE_STATUS.REJECTED:
      return { name: "Reagendamento rejeitado", color: "text-warning", bgColor: "bg-warning05", icon: "fas fa-times-circle", text: `não foi confirmado por ${by}` };
    case RESCHEDULE_STATUS.CANCELED:
      return { name: "Reagendamento cancelado", color: "text-danger", bgColor: "bg-danger05", icon: "fas fa-times-circle", text: "foi cancelado" };
    case RESCHEDULE_STATUS.ACCEPTED:
      return { name: "Reagendamento confirmado", color: "text-success", bgColor: "bg-success05", icon: "fas fa-check-circle", text: `foi confirmado por ${by}` };
    default:
      return { name: "Sem status", color: "primary" };
  }
}

export function formatRecurrency(recurrency: any) {
  try {
    const frequency =
      recurrency.frequency == APPOINTMENT_FREQUENCY.WEEKLY && recurrency.interval == 2
        ? APPOINTMENT_FREQUENCY.FORTNIGHT
        : recurrency.frequency;
    const next_appointment_date = recurrency.next_appointment_date?.date
      ? recurrency.next_appointment_date.date.toFormattedDate(11)
      : null;
    const labels: Record<string, string> = {
      [APPOINTMENT_FREQUENCY.SINGLE]: "Avulsa",
      [APPOINTMENT_FREQUENCY.WEEKLY]: "Semanal",
      [APPOINTMENT_FREQUENCY.FORTNIGHT]: "Quinzenal",
      [APPOINTMENT_FREQUENCY.MONTHLY]: "Mensal",
    };
    return { frequency, frequency_str: labels[frequency] ?? "", next_appointment_date };
  } catch (e) {
    console.error("formatRecurrency", e);
    return recurrency;
  }
}

/** Campos derivados do legado, calculados sobre os campos crus da sessão. */
export function deriveAppointmentFields(a: any) {
  const out: any = {};
  if (a.status) out.formattedStatus = formatAppointmentStatus(a.status);

  if (a.ratings) {
    const mine = a.ratings.find((r: any) => r.rating_by == "user");
    out.my_rating = mine
      ? { ...mine, trouble: Boolean(mine.trouble), call_completed: Boolean(mine.call_completed) }
      : undefined;
    out.hasChat = a.chat && a.chat?.status == "active" && !a.user?.deleted_at;
  }

  if (a.type == APPOINTMENT_TYPES.IMEDIATE) out.formatted_type = "Imediata";
  else if (a.first_appointment) out.formatted_type = "Conversa inicial";
  else out.formatted_type = "Agendado";

  const sch = a.reschedule_requests?.[0];
  if (sch && sch.status != RESCHEDULE_STATUS.CANCELED) {
    const madeByPsi = sch.user_id === a.psychologist_id;
    out.formattedReschedule = { ...sch, ...formatRescheduleStatus(sch.status, madeByPsi), madeByPsi };
  }

  out.hasRecurrency = !!(a.recurrency_id && a.recurrency);
  if (out.hasRecurrency) out.formattedRecurrency = formatRecurrency(a.recurrency);

  out.showCallLink =
    a.can_enter ||
    (a.can_enter && (a.status == APPOINTMENT_STATUS.ANDAMENTO || a.status != APPOINTMENT_STATUS.AGUARDANDO));
  // regra do main (o core também conta requires_action e exige cartão)
  out.paymentError = a.pg_status == "requires_payment_method";
  return out;
}

export function toAppointmentView(appointment: Appointment): any {
  const a: any = appointment;
  const psychologist = a.psychologist ? toPsiView(a.psychologist) : a.psychologist;
  const derived = deriveAppointmentFields(a);
  const view: any = { ...a, psychologist, ...derived };
  // o legado coloca o psi completo no chat (o core deixa só id/nome)
  if (derived.hasChat) view.chat = { ...a.chat, psychologist };
  return view;
}


// ---- lista de sessões no padrão do app RN ----

/** "25/11 - Quarta-feira às 09h15", como na lista de sessões do app. */
export function appointmentListDate(date: string) {
  const m = moment(date);
  const weekday = m.format("dddd");
  return `${m.format("DD/MM")} - ${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} às ${m.format("HH[h]mm")}`;
}

// cores do core (hex) → classes do tema
const STATUS_COLOR_CLASS: Record<string, string> = {
  "#FE8F00": "text-warning",
  "#00A550": "text-success",
  "#123766": "text-primary",
  "#E13C31": "text-danger",
  "#FBA486": "text-secondary",
};

/** Texto e cor do status na lista, com as regras do core (as mesmas do app). */
export function appointmentListStatus(a: any) {
  const item = { status: a?.status, rescheduleRequest: a?.rescheduleRequest ?? a?.formattedReschedule };
  return {
    label: getAppointmentStatusLabel(item as any),
    colorClass: STATUS_COLOR_CLASS[getAppointmentStatusColor(item as any)] ?? "text-primary",
  };
}
